import React, { InputHTMLAttributes, ReactNode, forwardRef } from "react";

/**
* Input
* ------------------
* A custom input component extending the stand HTML input element.
* Add variants for different input types.
* 
* Use for single line input field, both text and number.
*/

const inputVariants = {
  default:
    "border border-neutral-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-1 focus:ring-black",
  rounded:
    "border border-neutral-300 rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500",
  underlined:
    "border-b border-neutral-300 px-0 py-2 focus:outline-none focus:border-blue-500",
  floating:
    "border border-neutral-300 rounded-lg px-3 pt-6 pb-2 focus:outline-none focus:ring-2 focus:ring-black",
};

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  description?: string;
  icon?: ReactNode;
  variant?: keyof typeof inputVariants;
  error?: string;
}

// Using forwardRef to support React Hook Form
export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, description, icon, variant = "default", className, error, ...props }, ref) => {
    const isFloating = variant === "floating";

    // pull placeholder out so we don't forward it in floating mode
    const { placeholder, ...rest } = props;

    return (
      <div className="flex flex-col w-full relative">
        <div className="relative flex items-center">
          {isFloating ? (
            <>
              <input
                ref={ref}
                placeholder=" " // only to enable :placeholder-shown
                className={`${inputVariants[variant]} w-full peer ${className || ""} ${
                  error ? "border-red-500 focus:ring-red-500" : ""
                }`}
                {...rest}
              />
              <label
                className={`absolute left-3 top-2 text-neutral-400 text-sm transition-all pointer-events-none
                  peer-placeholder-shown:top-[13px] peer-placeholder-shown:text-base
                  peer-focus:top-2 peer-focus:text-sm peer-focus:text-neutral-600`}
              >
                {label ?? placeholder}
              </label>
            </>
          ) : (
            <div className="w-full">
              {label && (
                <label className="mb-1 text-sm font-medium text-neutral-700">
                  {label}
                </label>
              )}
              <input
                ref={ref}
                className={`${inputVariants[variant]} w-full ${icon ? "pl-10" : ""} ${className || ""} ${
                  error ? "border-red-500 focus:ring-red-500" : ""
                }`}
                placeholder={placeholder}
                {...rest}
              />
            </div>
          )}

          {icon && !isFloating && <span className="absolute left-3">{icon}</span>}
        </div>

        {description && !error && <small className="mt-1 text-xs text-gray-500">{description}</small>}
        {error && <small className="mt-1 text-xs text-red-500">{error}</small>}
      </div>
    );
  }
);


Input.displayName = "Input";
